import { hashQuantizedState, quantizeVehicleState, sha256Hex, } from "../simulation/version.js";
import { validateReplayControlBits } from "./controlBits.js";
export const REPLAY_SCHEMA_VERSION_NUMBER = 1;
export const REPLAY_TICK_RATE = 60;
export const MAX_REPLAY_TICKS = 60 * 60 * 30;
export const MAX_REPLAY_EVENTS = 120_000;
export const MAX_REPLAY_CHECKPOINTS = 256;
export const MAX_REPLAY_STATE_HASHES = 20_000;
export const MAX_REPLAY_PAYLOAD_BYTES = 8 * 1024 * 1024;
export function canonicalReplayObject(replay) {
    validateReplayEnvelope(replay);
    return {
        schemaVersion: replay.schemaVersion,
        tickRate: replay.tickRate,
        trackId: replay.trackId,
        carId: replay.carId,
        trackMapHash: replay.trackMapHash,
        carConfigHash: replay.carConfigHash,
        ...(replay.rulesetId ? { rulesetId: replay.rulesetId } : {}),
        attemptId: replay.attemptId,
        startTick: replay.startTick,
        endTick: replay.endTick,
        initialState: canonicalState(replay.initialState),
        events: replay.events.map((event) => ({
            tick: event.tick,
            controls: event.controls,
        })),
        checkpoints: replay.checkpoints.map((checkpoint) => ({
            index: checkpoint.index,
            tick: checkpoint.tick,
        })),
        stateHashes: replay.stateHashes.map((entry) => ({
            tick: entry.tick,
            hash: entry.hash,
        })),
    };
}
export function canonicalReplayJson(replay) {
    return JSON.stringify(canonicalReplayObject(replay));
}
export async function replayPayloadHash(replay) {
    const payload = new TextEncoder().encode(canonicalReplayJson(replay));
    if (payload.byteLength > MAX_REPLAY_PAYLOAD_BYTES) {
        throw new Error("Replay payload is too large");
    }
    return sha256Hex(payload);
}
export function parseReplayJson(serialized) {
    if (new TextEncoder().encode(serialized).byteLength > MAX_REPLAY_PAYLOAD_BYTES) {
        throw new Error("Replay payload is too large");
    }
    let value;
    try {
        value = JSON.parse(serialized);
    }
    catch {
        throw new Error("Replay payload is not valid JSON");
    }
    validateReplayEnvelope(value);
    return value;
}
export function validateReplayEnvelope(value) {
    if (!isRecord(value))
        throw new Error("Replay payload must be an object");
    if (value.schemaVersion !== REPLAY_SCHEMA_VERSION_NUMBER)
        throw new Error("Unsupported replay schema");
    if (value.tickRate !== REPLAY_TICK_RATE)
        throw new Error("Unsupported replay tick rate");
    for (const key of ["trackId", "carId", "startTick", "endTick"]) {
        if (!isSafeInteger(value[key]) || Number(value[key]) < 0)
            throw new Error(`Invalid replay ${key}`);
    }
    if (Number(value.trackId) < 1 || Number(value.carId) < 1) {
        throw new Error("Replay track and car ids must be positive");
    }
    if (Number(value.endTick) < Number(value.startTick))
        throw new Error("Replay tick range is invalid");
    if (Number(value.endTick) - Number(value.startTick) > MAX_REPLAY_TICKS) {
        throw new Error("Replay duration exceeds the supported limit");
    }
    for (const key of ["trackMapHash", "carConfigHash", "attemptId"]) {
        if (typeof value[key] !== "string" ||
            value[key].length === 0 ||
            value[key].length > 256) {
            throw new Error(`Invalid replay ${key}`);
        }
    }
    if (value.rulesetId !== undefined &&
        (typeof value.rulesetId !== "string" ||
            value.rulesetId.length === 0 ||
            value.rulesetId.length > 128))
        throw new Error("Invalid replay rulesetId");
    assertState(value.initialState);
    assertEvents(value.events, Number(value.startTick), Number(value.endTick));
    assertCheckpoints(value.checkpoints, Number(value.startTick), Number(value.endTick));
    assertStateHashes(value.stateHashes, Number(value.startTick), Number(value.endTick));
}
export function stateHash(state) {
    return hashQuantizedState(quantizeVehicleState(state));
}
function assertEvents(value, startTick, endTick) {
    if (!Array.isArray(value) ||
        value.length === 0 ||
        value.length > MAX_REPLAY_EVENTS)
        throw new Error("Invalid replay events");
    let previousTick = startTick - 1;
    for (const [index, event] of value.entries()) {
        if (!isRecord(event) ||
            !isSafeInteger(event.tick) ||
            !isSafeInteger(event.controls)) {
            throw new Error("Invalid replay event");
        }
        if (event.tick < startTick ||
            event.tick > endTick ||
            event.tick <= previousTick) {
            throw new Error("Replay events must be sorted and in range");
        }
        if (index === 0 && event.tick !== startTick) {
            throw new Error("Replay must begin with a control event at startTick");
        }
        validateReplayControlBits(event.controls);
        previousTick = event.tick;
    }
}
function assertCheckpoints(value, startTick, endTick) {
    if (!Array.isArray(value) || value.length > MAX_REPLAY_CHECKPOINTS)
        throw new Error("Invalid replay checkpoints");
    let previousTick = startTick - 1;
    let previousIndex = -1;
    for (const checkpoint of value) {
        if (!isRecord(checkpoint) ||
            !isSafeInteger(checkpoint.index) ||
            !isSafeInteger(checkpoint.tick)) {
            throw new Error("Invalid replay checkpoint");
        }
        if (checkpoint.index < 0 ||
            checkpoint.index > 2 ||
            checkpoint.tick < startTick ||
            checkpoint.tick > endTick ||
            checkpoint.tick <= previousTick) {
            throw new Error("Replay checkpoints must be sorted and in range");
        }
        if (previousIndex < 0 && checkpoint.index !== 0) {
            throw new Error("Replay checkpoints must begin with red index 0");
        }
        if (previousIndex >= 0 && checkpoint.index !== (previousIndex + 1) % 3) {
            throw new Error("Replay checkpoints must follow red, green, blue order");
        }
        previousTick = checkpoint.tick;
        previousIndex = checkpoint.index;
    }
}
function assertStateHashes(value, startTick, endTick) {
    if (!Array.isArray(value) || value.length > MAX_REPLAY_STATE_HASHES)
        throw new Error("Invalid replay state hashes");
    let previousTick = startTick - 1;
    for (const entry of value) {
        if (!isRecord(entry) ||
            !isSafeInteger(entry.tick) ||
            typeof entry.hash !== "string" ||
            !/^[0-9a-f]{8}$/i.test(entry.hash)) {
            throw new Error("Invalid replay state hash");
        }
        if (entry.tick < startTick ||
            entry.tick > endTick ||
            entry.tick <= previousTick) {
            throw new Error("Replay state hashes must be sorted and in range");
        }
        previousTick = entry.tick;
    }
}
function assertState(value) {
    if (!isRecord(value) || !isRecord(value.position))
        throw new Error("Invalid replay initial state");
    for (const key of ["x", "y"])
        assertFiniteNumber(value.position[key]);
    for (const key of [
        "headingRad",
        "longitudinalSpeed",
        "lateralSpeed",
        "yawRate",
        "steeringAngleRad",
    ]) {
        assertFiniteNumber(value[key]);
    }
    if (value.tires === undefined) {
        throw new Error("Replay state must include tires");
    }
    if (value.tires !== undefined) {
        if (!isRecord(value.tires))
            throw new Error("Invalid replay tire state");
        for (const key of ["frontLeft", "frontRight", "rearLeft", "rearRight"]) {
            assertFiniteNumber(value.tires[key]);
            if (Number(value.tires[key]) < 0 || Number(value.tires[key]) > 100)
                throw new Error("Replay tire state is out of range");
        }
    }
    if (value.pitServiceContact !== undefined &&
        typeof value.pitServiceContact !== "boolean")
        throw new Error("Replay pit service contact must be boolean");
    if (value.pitStopVisitCount !== undefined) {
        if (!isSafeInteger(value.pitStopVisitCount) ||
            Number(value.pitStopVisitCount) < 0 ||
            Number(value.pitStopVisitCount) > 255)
            throw new Error("Replay pit stop count is out of range");
    }
    const typedState = value;
    const quantized = quantizeVehicleState(typedState);
    if (JSON.stringify(canonicalState(typedState)) !==
        JSON.stringify(canonicalState(quantized))) {
        throw new Error("Replay initial state must be quantized");
    }
}
function canonicalState(state) {
    const result = {
        position: { x: state.position.x, y: state.position.y },
        headingRad: state.headingRad,
        longitudinalSpeed: state.longitudinalSpeed,
        lateralSpeed: state.lateralSpeed,
        yawRate: state.yawRate,
        steeringAngleRad: state.steeringAngleRad,
    };
    if (state.tires) {
        result.tires = {
            frontLeft: state.tires.frontLeft,
            frontRight: state.tires.frontRight,
            rearLeft: state.tires.rearLeft,
            rearRight: state.tires.rearRight,
        };
    }
    if (state.pitServiceContact !== undefined)
        result.pitServiceContact = state.pitServiceContact;
    if (state.pitStopVisitCount !== undefined)
        result.pitStopVisitCount = state.pitStopVisitCount;
    return result;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isSafeInteger(value) {
    return typeof value === "number" && Number.isSafeInteger(value);
}
function assertFiniteNumber(value) {
    if (typeof value !== "number" || !Number.isFinite(value))
        throw new Error("Replay state contains a non-finite number");
}
